import { whenText } from './claude-fleet.live'
import { runnerActionStatus } from './claude-fleet.runner-actions'
import { usageWindowText } from './claude-fleet.runner-health'
import type { FleetRunnerHealthItem } from './claude-fleet.runner-health'
import { isRunnerOwner, runnerItemsTodayLabel, runnerOwnTodayLabel, runnerPausedReason } from './claude-fleet.runners'
import type { FleetRunner } from './claude-fleet.types'
import type { FleetRunnerLiveJob, FleetRunnerLiveRecent } from './claude-fleet.runner-live.types'
import { formatDateTime, formatStepCost } from './claude-fleet.utils'

/**
 * What a laptop runner is doing now, from the watch screen its heartbeat carries (`liveView`): the Runners list's
 * Now and Usage cells, the drawer's Live section, and the Remote Control session its /runner window started. Every
 * field is optional, so a laptop or fleet older than the watch screen still gets what its heartbeat has.
 */

/** The longest message the fleet passes to a /runner window. */
export const RUNNER_SAY_MAX_LENGTH = 2000

/** Where Remote Control sessions are listed by name when the window's own address is not known. */
export const REMOTE_CONTROL_LIST_URL = 'https://claude.ai/code'

/** The most lines of the window's conversation or watch screen the pill's hover shows. */
const TAIL_LINES = 8

/** "42s", "7m", "1h05m": the laptop's watch screen's own format (bin/laptop_runner.py `_ago`), so the two read alike. */
export function formatElapsedSeconds(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds))
  if (s < 60) return `${s}s`
  if (s < 3600) return `${Math.floor(s / 60)}m`
  return `${Math.floor(s / 3600)}h${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}m`
}

/**
 * What the machine runs now: its fleet jobs (`running`, which take slots) and the background agents its /runner window
 * has going, which include its fleet jobs while it runs them in the window, plus the agents it started itself (a QA
 * sweep, its /pr-loop reviews). The larger of the two, since the two overlap; a headless fleet job beside window
 * agents is the one case it undercounts.
 */
export function runnerBusy(runner: Pick<FleetRunner, 'running' | 'liveView'>): { total: number; fleet: number } {
  const fleet = runner.running ?? 0
  return { total: Math.max(fleet, runner.liveView?.window?.agents ?? 0), fleet }
}

/**
 * One line for the Runners list: the laptop's own line, else what its heartbeat says ("Paused", "2 running",
 * "Idle: waiting for work"); null for an offline runner or one that says nothing. A pause is just "Paused" here:
 * the Capacity column beside it already gives the reason.
 */
export function runnerDoingLine(runner: FleetRunner): string | null {
  if (!runner.live) return null
  const doing = runner.liveView?.doing?.trim()
  if (doing) return /^paused\b/i.test(doing) ? 'Paused' : doing
  if (runnerPausedReason(runner)) return 'Paused'
  const { total } = runnerBusy(runner)
  if (total > 0) return `${total} running`
  return runner.freeSlots > 0 ? 'Idle: waiting for work' : null
}

export interface RunnerDayShare {
  usedPct: number
  sharePct: number
}

/** Today's use of the 7-day window against today's share; null when it is unknown or the owner has no daily share. */
export function runnerDayShare(runner: Pick<FleetRunner, 'liveView' | 'health'>): RunnerDayShare | null {
  const sent = runner.liveView?.dayShare
  if (typeof sent?.sharePct === 'number' && sent.sharePct > 0) {
    return { usedPct: sent.usedPct ?? 0, sharePct: sent.sharePct }
  }
  const limits = runner.health?.limits
  if (typeof limits?.dayLimitPct === 'number' && limits.dayLimitPct > 0) {
    return { usedPct: limits.dayUsedPct ?? 0, sharePct: limits.dayLimitPct }
  }
  return null
}

export interface RunnerUsageLine {
  key: 'usage-5h' | 'usage-7d' | 'day-share'
  text: string
  /** When the window resets, for the hover. */
  title?: string
  warning?: boolean
}

/** The Usage cell: "5h 42% · resets in 3h", "7d 61% · resets Thu 10:00", "Today 4% of 15%"; one line per reading. */
export function runnerUsageLines(runner: Pick<FleetRunner, 'liveView' | 'health'>, now: number): RunnerUsageLine[] {
  const usage = runner.health?.usage
  const lines: RunnerUsageLine[] = []
  const windows = [
    ['usage-5h', '5h', usage?.h5],
    ['usage-7d', '7d', usage?.d7]
  ] as const
  for (const [key, name, window] of windows) {
    if (!window) continue
    lines.push({
      key,
      text: `${name} ${usageWindowText(window, now)}`,
      title: new Date(window.resetsAt).toLocaleString()
    })
  }
  const share = runnerDayShare(runner)
  if (share) {
    lines.push({
      key: 'day-share',
      text: `Today ${Math.round(share.usedPct)}% of ${Math.round(share.sharePct)}%`,
      title: "Today's use of the 7-day window, against the share of it the owner allows each day",
      warning: share.usedPct >= share.sharePct
    })
  }
  return lines
}

export interface RunnerRemoteControl {
  /** The session's name, as the Claude app lists it. */
  name: string
  /** The session itself when the laptop knows its address (only ever on claude.ai), else the list it is found in by name. */
  url: string
  /** True when `url` is the list rather than the session. */
  listed: boolean
}

/** The Remote Control session the machine's /runner window started; null while no window holds it or it has none. */
export function runnerRemoteControl(runner: Pick<FleetRunner, 'liveView'>): RunnerRemoteControl | null {
  const window = runner.liveView?.window
  const name = window?.remoteControl?.trim()
  if (!window || !name) return null
  const url = window.remoteControlUrl?.trim()
  return url && url.startsWith('https://claude.ai/')
    ? { name, url, listed: false }
    : { name, url: REMOTE_CONTROL_LIST_URL, listed: true }
}

/**
 * The session only for a live runner's owner: it lives on their own Claude account, so nobody else can open it, and an
 * offline runner's last report may still name a window that has closed.
 */
export function ownRunnerRemoteControl(
  runner: Pick<FleetRunner, 'owner' | 'live' | 'liveView'>,
  email: string | null | undefined
): RunnerRemoteControl | null {
  return runner.live && isRunnerOwner(runner.owner, email) ? runnerRemoteControl(runner) : null
}

/** Whether a /runner window holds the machine now, so a message can reach it. */
export function runnerWindowHeld(runner: Pick<FleetRunner, 'liveView'>): boolean {
  return Boolean(runner.liveView?.window)
}

export type RunnerLiveFact = Omit<FleetRunnerHealthItem, 'short'>

/** A lane switched to drain, review-only or stage-only, or paused, delivers nothing, as a starved lane does. */
const LANE_NOT_DELIVERING = /not taking new tickets|lane paused/i

/** The drawer's Live facts, in the order the watch screen shows them; today's share is Health's to show. */
export function runnerLiveFacts(runner: FleetRunner, now: number): RunnerLiveFact[] {
  const view = runner.liveView
  const remote = runnerRemoteControl(runner)
  const facts: (RunnerLiveFact | null)[] = [
    view?.now ? { key: 'now', label: 'Status', value: view.now } : null,
    {
      key: 'today',
      label: 'Today',
      value: [`${runnerItemsTodayLabel(runner)} fleet items${typeof runner.cap === 'number' ? '' : ' · no cap'}`, runnerOwnTodayLabel(runner)]
        .filter(Boolean)
        .join(' · ')
    },
    view?.slotsLine ? { key: 'slots', label: 'Slots now', value: view.slotsLine } : null,
    view?.usageLine ? { key: 'usage', label: 'Usage', value: view.usageLine } : null,
    laneFact(view?.laneLine),
    view?.idle ? { key: 'idle', label: 'Idle', value: view.idle } : null,
    windowFact(runner),
    remote
      ? {
          key: 'remote-control',
          label: 'Remote Control',
          value: remote.listed
            ? `"${remote.name}" in the Claude app or at claude.ai/code`
            : `"${remote.name}"`,
          title: 'Read and type into the /runner window from the Claude app on a phone, or from claude.ai/code'
        }
      : null,
    lastActionFact(runner, now)
  ]
  return facts.filter((fact): fact is RunnerLiveFact => fact !== null)
}

/** "Delivery lane: lane 1 · not taking new tickets: drain on (…) · 0 delivered today", under its own label. */
function laneFact(line: string | undefined): RunnerLiveFact | null {
  const value = line?.trim().replace(/^delivery lane:\s*/i, '')
  if (!value) return null
  const fact: RunnerLiveFact = { key: 'lane', label: 'Delivery lane', value }
  // The warning pill cuts a long line short, so its hover carries the whole line and the command that turns it off.
  return LANE_NOT_DELIVERING.test(value) ? { ...fact, warning: true, title: value } : fact
}

function windowFact(runner: FleetRunner): RunnerLiveFact | null {
  const window = runner.liveView?.window
  if (!runner.liveView) return null
  if (!window) return { key: 'window', label: 'Window', value: 'No /runner window holds the machine: it works in the background' }
  const since = formatDateTime(window.since) ? ` since ${formatDateTime(window.since)}` : ''
  return { key: 'window', label: 'Window', value: `A /runner window holds the machine${since}` }
}

function lastActionFact(runner: FleetRunner, now: number): RunnerLiveFact | null {
  const status = runnerActionStatus(runner, now)
  return status ? { key: 'last-action', label: 'Last action', value: status.replace(/^Last action: /, '') } : null
}

export interface RunnerLiveJobView {
  key: string
  title: string
  step?: string
}

/** The jobs it runs now, longest-running first as the laptop sends them: "PR review adx#1153 · 4m" and its step. */
export function runnerLiveJobs(jobs: FleetRunnerLiveJob[] | undefined): RunnerLiveJobView[] {
  return (jobs ?? []).map((job, index) => ({
    key: job.offer || `job-${index}`,
    title: [jobName(job.activity, job.label), typeof job.elapsedS === 'number' ? formatElapsedSeconds(job.elapsedS) : '']
      .filter(Boolean)
      .join(' · '),
    step: job.step?.trim() || undefined
  }))
}

/** What it finished lately, newest first: "done: PR review adx#1153 · 12m ago · $0.84". */
export function runnerLiveRecent(recent: FleetRunnerLiveRecent[] | undefined, now: number): { key: string; text: string }[] {
  return [...(recent ?? [])].reverse().map((entry, index) => {
    const ago = typeof entry.endedAt === 'number' ? whenText(new Date(entry.endedAt * 1000).toISOString(), now) : ''
    const cost = formatStepCost(entry.costUsd)
    const head = [entry.ended, jobName(entry.activity, entry.label)].filter(Boolean).join(': ')
    return { key: `${entry.offer ?? index}-${entry.endedAt ?? index}`, text: [head, ago, cost].filter(Boolean).join(' · ') }
  })
}

function jobName(activity: string | undefined, label: string | undefined): string {
  return [activity, label].filter(Boolean).join(' ')
}

/** The pill hover's last lines: the window's conversation or the watch screen, newest last. */
export function runnerTail(runner: Pick<FleetRunner, 'liveView'> | null): string[] {
  return (runner?.liveView?.tail ?? []).filter(line => typeof line === 'string' && line.trim()).slice(-TAIL_LINES)
}
